export type ParentRequestResult = {
    requestId: string
    status: number
    data?: unknown
}

type PendingRequest = {
    resolve: (data: unknown) => void
    reject: (error: unknown) => void
    timeout: ReturnType<typeof setTimeout>
}

export const PARENT_REQUEST_TIMEOUT = 30_000

const pendingRequests = new Map<string, PendingRequest>()

export const waitForParentResult = <T>(requestId: string, timeoutMs = PARENT_REQUEST_TIMEOUT) => new Promise<T>((resolve, reject) => {
    // a parent that never answers (older dashboard, closed builder) must not leave the caller waiting
    const timeout = setTimeout(() => {
        pendingRequests.delete(requestId)
        reject(new Error(`Parent did not answer request ${requestId}`))
    }, timeoutMs)

    pendingRequests.set(requestId, { resolve: resolve as (data: unknown) => void, reject, timeout })
})

export const settleParentRequest = ({ requestId, status, data }: ParentRequestResult) => {
    const request = pendingRequests.get(requestId)
    if (!request) {
        return
    }

    pendingRequests.delete(requestId)
    clearTimeout(request.timeout)

    if (status >= 200 && status < 300) {
        request.resolve(data)
        return
    }

    // shaped like an axios error, so callers handle a proxied failure the same way as their own requests
    request.reject(Object.assign(new Error(`Parent request ${requestId} failed with status ${status}`), {
        isAxiosError: true,
        response: { status, data },
    }))
}
